import {
  type ProductInterface,
  getDiscountPrices,
} from 'dotcom-core/src/helpers/product';
import type { ProductVariant } from '~/graphql';
import { useIntl } from '~/helpers/intl';
import { isTruthy } from '~/helpers/predicate';

type PriceListingProps = {
  product: ProductInterface;
  variant?: ProductVariant | null;
};

export function PriceListing({ product, variant }: PriceListingProps) {
  const { t, tPrice } = useIntl();

  const finalPrice = variant ? variant?.price?.final : product?.price?.final;

  const { previousPrice, savingsPrice } = getDiscountPrices({
    variant,
    product,
  });

  const showSavings =
    isTruthy(savingsPrice && savingsPrice?.value > 0) &&
    isTruthy(previousPrice && previousPrice?.value > 0);

  return (
    <div
      data-testid="price-listing"
      className="w-full flex font-medium items-center justify-center"
    >
      <p data-testid="final-price" className="text-3xl lg:text-2xl font-medium">
        {finalPrice && tPrice({ price: finalPrice })}
      </p>

      {showSavings && (
        <div
          className="ml-2 flex-col items-center"
          data-testid="previous-price"
        >
          {previousPrice && (
            <PreviousPrice product={product} variant={variant} />
          )}

          <div className="text-2xs text-savings" data-testid="saving-price">
            {savingsPrice &&
              t('planSelector.savings', {
                fallback: 'You save {value}',
                values: {
                  value: tPrice({
                    price: savingsPrice,
                  }),
                },
              })}
          </div>
        </div>
      )}
    </div>
  );
}

type Props = {
  variant?: ProductVariant | null;
  product: ProductInterface;
};

export function PreviousPrice({ product, variant }: Props) {
  const { tPrice, tReact } = useIntl();

  const { previousPriceMessage, previousPrice } = getDiscountPrices({
    product,
    variant,
  });

  return (
    <div className="text-2xs text-grey-60 mr-0.5">
      {previousPriceMessage === 'regularPrice' &&
        tReact('pdp.regularPrice', {
          fallback: 'Regular price {value}',
          values: {
            value: (
              <span className="line-through" data-testid="regular-price">
                {isTruthy(previousPrice) &&
                  tPrice({
                    price: previousPrice,
                  })}
              </span>
            ),
          },
        })}

      {previousPriceMessage === 'regularUpdatePrice' &&
        tReact('pdp.regularLoyaltyPrice', {
          fallback: 'Regular update price {value}',
          values: {
            value: (
              <span className="line-through" data-testid="regular-update-price">
                {isTruthy(previousPrice) &&
                  tPrice({
                    price: previousPrice,
                  })}
              </span>
            ),
          },
        })}

      {previousPriceMessage === 'totalPriceSeparately' &&
        tReact('pdp.totalBundlePrice', {
          fallback: 'Total if purchased separately {value}',
          values: {
            value: (
              <span className="line-through" data-testid="all-bundle-price">
                {isTruthy(previousPrice) &&
                  tPrice({
                    price: previousPrice,
                  })}
              </span>
            ),
          },
        })}

      {previousPriceMessage === 'fullVersionPrice' &&
        tReact('pdp.fullVersionPrice', {
          fallback: 'Full version price {value}',
          values: {
            value: (
              <span className="line-through" data-testid="previous-price-msg">
                {isTruthy(previousPrice) &&
                  tPrice({
                    price: previousPrice,
                  })}
              </span>
            ),
          },
        })}
    </div>
  );
}
